import dayjs from "dayjs";
import PropTypes from "prop-types";

import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

import { formatDate, formatDateTime, formatMoney } from "../../shared";

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
    marginTop: 16,
    marginBottom: 12,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#ececec",
    fontFamily: "Helvetica-Bold",
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tableRow: {
    flexDirection: "row",
    borderColor: "#ececec",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  cell: {
    padding: 4,
  },
  attributeHeader: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginTop: 8,
  },
  infoSection: {
    flexDirection: "row",
    marginVertical: 16,
  },
  infoItem: {
    flex: 1,
  },
  totalSection: {
    alignItems: "flex-end",
    marginTop: 16,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    minWidth: 200,
    marginBottom: 4,
  },
  grandTotalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    minWidth: 200,
    marginBottom: 4,
    fontFamily: "Helvetica-Bold",
    fontSize: 12,
    borderTopWidth: 1,
    borderTopColor: "#364F6B",
    paddingTop: 4,
  },
});

export const PurchaseOrderStandardPdf = ({ purchaseOrder, user }) => {
  const subtotal = purchaseOrder.purchaseOrderProductVariants.reduce(
    (sum, variant) => {
      const itemTotal =
        variant.quantity * variant.costPerUnit - (variant.discount || 0);
      return sum + itemTotal;
    },
    0
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <View>
            <Text style={styles.header}>Pesanan Pembelian</Text>
            <Text
              style={{
                fontSize: 12,
                color: "#364F6B",
                fontFamily: "Helvetica-Bold",
                marginTop: 4,
              }}
            >
              {purchaseOrder.referenceNumber}
            </Text>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Text
              style={{
                fontSize: 10,
                color: "#364F6B",
                textAlign: "right",
              }}
            >
              Dicetak oleh: {user.name}
            </Text>
            <Text
              style={{
                fontSize: 10,
                color: "#364F6B",
                marginTop: 2,
                textAlign: "right",
              }}
            >
              Tanggal cetak: {formatDateTime(dayjs())}
            </Text>
          </View>
        </View>

        {/* Information Section */}
        <View style={styles.infoSection}>
          <View style={styles.infoItem}>
            <Text>Pembeli</Text>
            <Text style={styles.attributeHeader}>
              {purchaseOrder.buyer.name}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Gudang</Text>
            <Text style={styles.attributeHeader}>
              {purchaseOrder.warehouse.name}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Penjual</Text>
            <Text style={styles.attributeHeader}>
              {purchaseOrder.seller.name}
            </Text>
          </View>
        </View>

        <View style={styles.infoSection}>
          <View style={styles.infoItem}>
            <Text>Tanggal Transaksi</Text>
            <Text style={styles.attributeHeader}>
              {formatDate(purchaseOrder.transactionDate)}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Syarat Pembayaran</Text>
            <Text style={styles.attributeHeader}>
              {purchaseOrder.paymentTerm?.name ?? "-"}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Kena Pajak</Text>
            <Text style={styles.attributeHeader}>
              {purchaseOrder.taxable ? "Ya" : "Tidak"}
            </Text>
          </View>
        </View>

        <View style={styles.infoSection}>
          <View style={[styles.infoItem, { flex: 2 }]}>
            <Text>Catatan</Text>
            <Text style={styles.attributeHeader}>
              {purchaseOrder.note ?? "-"}
            </Text>
          </View>
        </View>

        {/* Products Table */}
        <Text style={styles.sectionTitle}>Detail Produk Varian</Text>
        <View style={styles.tableHeader}>
          <Text style={[styles.cell, { flex: 7 }]}>Nama</Text>
          <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
            Qty
          </Text>
          <Text style={[styles.cell, { flex: 1 }]}>Satuan</Text>
          <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
            @Harga
          </Text>
          <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
            Diskon
          </Text>
          <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
            Total
          </Text>
        </View>
        {purchaseOrder.purchaseOrderProductVariants.map((variant) => {
          const itemTotal =
            variant.quantity * variant.costPerUnit - (variant.discount || 0);
          return (
            <View style={styles.tableRow} key={variant.id}>
              <View style={[styles.cell, { flex: 7 }]}>
                <Text>{variant.name}</Text>
                <Text style={{ color: "#666", fontSize: 8, marginTop: 2 }}>
                  {variant.sku}
                </Text>
              </View>
              <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
                {variant.quantity}
              </Text>
              <Text style={[styles.cell, { flex: 1 }]}>
                {variant.product.unitOfMeasurement.name}
              </Text>
              <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                {variant.costPerUnit}
              </Text>
              <Text style={[styles.cell, { flex: 1, textAlign: "right" }]}>
                {variant.discount || 0}
              </Text>
              <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
                {itemTotal}
              </Text>
            </View>
          );
        })}

        {/* Totals Section */}
        <View style={styles.totalSection}>
          <View style={styles.totalRow}>
            <Text>Subtotal:</Text>
            <Text>{formatMoney(subtotal)}</Text>
          </View>
          {purchaseOrder.totalDiscount > 0 && (
            <View style={styles.totalRow}>
              <Text>Total Diskon:</Text>
              <Text>{formatMoney(purchaseOrder.totalDiscount)}</Text>
            </View>
          )}
          {purchaseOrder.taxable && (
            <View style={styles.totalRow}>
              <Text>Pajak ({purchaseOrder.taxPercentage || 0}%):</Text>
              <Text>{formatMoney(purchaseOrder.taxAmount || 0)}</Text>
            </View>
          )}
          <View style={styles.grandTotalRow}>
            <Text>Total:</Text>
            <Text>{formatMoney(purchaseOrder.grandTotal)}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

PurchaseOrderStandardPdf.propTypes = {
  purchaseOrder: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};
